import {
  AuthenticationType,
  CheckoutCustomer,
} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_customer.types';
import {
  LogInWithEmailV1Response,
  LogInWithFacebookV1Response,
  RegisterWithEmailV1Response,
  SignInWithAppleV1Response,
} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.types';
import {CustomerGender} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {
  Customer,
  Customer_CustomerGender,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/customer.types';
import {LogInWithCodeResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/authentication/v1/service.types';
import type {AuthMedium, LoggedInUserData} from '@aboutyou/user';
import {UserGenders} from '@aboutyou/user';

export type AuthResponseTypes =
  | LogInWithEmailV1Response
  | RegisterWithEmailV1Response
  | SignInWithAppleV1Response
  | LogInWithFacebookV1Response
  | LogInWithCodeResponse;

export const normalizeAuthData = (response: AuthResponseTypes) => {
  if (isLegacyAuthResponse(response)) {
    return normalizeLegacyAuthData(response);
  } else {
    return normalizeTadaridaAuthData(response);
  }
};

const normalizeLegacyAuthData = (
  response: Exclude<AuthResponseTypes, LogInWithCodeResponse>,
) => {
  const {customer: rawCustomer, mobileAPIToken} = response;

  if (!rawCustomer || !mobileAPIToken) {
    throw new Error('Missing customer information');
  }

  const customerData = normalizeLegacyCheckoutCustomerData(rawCustomer);

  return {
    customerData,
    apiToken: mobileAPIToken,
  };
};

export const normalizeLegacyCheckoutCustomerData = (
  rawCustomer: CheckoutCustomer,
): LoggedInUserData => {
  if (!rawCustomer) {
    throw new Error('Missing customer information');
  }

  return {
    id: rawCustomer.id,
    gender:
      rawCustomer.gender === CustomerGender.MALE
        ? UserGenders.Male
        : UserGenders.Female,
    email: rawCustomer.email,
    emailHash: {
      emh: rawCustomer.emailHashes?.emh ?? rawCustomer.email,
      emhec: rawCustomer.emailHashes?.emhec ?? rawCustomer.email,
    },
    firstName: rawCustomer.firstName.trim() || undefined,
    lastName: rawCustomer.lastName.trim() || undefined,
    phone: rawCustomer.phone,
    birthday: `${rawCustomer.birthday}`,
    isExistingCustomer: !rawCustomer.isNewCustomer,
    loginType: (
      {
        [AuthenticationType.PASSWORD]: 'email',
        [AuthenticationType.FACEBOOK]: 'facebook',
        [AuthenticationType.APPLE]: 'apple',
      } as Record<AuthenticationType, AuthMedium>
    )[rawCustomer.authenticationType],
  };
};

const normalizeTadaridaAuthData = (response: LogInWithCodeResponse) => {
  const {customer: rawCustomer, session} = response;

  if (!rawCustomer || !session) {
    throw new Error('Missing customer information');
  }

  const customerData = normalizeTadaridaCustomerData(rawCustomer);

  return {
    customerData,
    apiToken: session.authToken,
  };
};

const normalizeTadaridaCustomerData = (
  rawCustomer: Customer,
): LoggedInUserData => {
  if (!rawCustomer) {
    throw new Error('Missing customer information');
  }

  return {
    id: rawCustomer.id,
    gender:
      rawCustomer.customerGender === Customer_CustomerGender.MALE
        ? UserGenders.Male
        : UserGenders.Female,
    email: rawCustomer.email,
    emailHash: {
      emh: rawCustomer.emailHash,
    },
    firstName: rawCustomer.firstName?.trim() || undefined,
    lastName: rawCustomer.lastName?.trim() || undefined,
    phone: rawCustomer.phoneNumber,
    birthday: `${rawCustomer.birthday}`,
    isExistingCustomer: !rawCustomer.isNewCustomer,
    loginType: rawCustomer.authenticationMethod.type,
  };
};

const isLegacyAuthResponse = (
  response: AuthResponseTypes,
): response is Exclude<AuthResponseTypes, LogInWithCodeResponse> => {
  return 'mobileAPIToken' in response;
};
